import { useState } from "react";
import { Plus, RotateCw, Trash2, Webhook } from "lucide-react";
import { toast } from "sonner";
import { Card, EmptyState, ErrorBanner, Notice, PageHeader, SecretReveal, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";

const EVENTS = ["email.accepted", "email.delivered", "email.bounced", "email.complained", "email.failed", "email.opened", "email.clicked", "email.unsubscribed"];

function Deliveries({ hook }) {
  const { data, error, loading, reload } = useLoad(() => api.get(`/app/webhooks/${hook.id}/deliveries`).then((r) => r.data), [hook.id]);
  const replay = async (id) => {
    try { await api.post(`/app/webhook-deliveries/${id}/replay`); toast.success("Replay scheduled"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  if (!data.length) return <p className="text-xs text-zinc-500" data-testid="deliveries-empty">No deliveries yet.</p>;
  return (
    <div className="overflow-x-auto"><table className="w-full min-w-[560px] text-left text-xs" data-testid="deliveries-table">
      <thead className="text-zinc-500"><tr><th className="py-2">Event</th><th>Status</th><th>Attempts</th><th>Last response</th><th>Created</th><th /></tr></thead>
      <tbody>{data.map((d) => {
        const last = d.history[d.history.length - 1];
        return (
          <tr key={d.id} className="border-t border-white/5">
            <td className="py-2 font-mono text-purple-200">{d.event_type}</td><td><StatusBadge status={d.status} /></td><td>{d.attempts}</td>
            <td className="text-zinc-400">{last ? last.status_code || last.error : "—"}</td><td className="text-zinc-500">{fmt(d.created_at)}</td>
            <td className="text-right">{["failed", "delivered", "skipped"].includes(d.status) && <Button size="sm" variant="ghost" onClick={() => replay(d.id)} className="h-7 text-xs hover:bg-white/5 hover:text-white" data-testid="webhook-replay-button">Replay</Button>}</td>
          </tr>);
      })}</tbody>
    </table></div>
  );
}

function HookCard({ hook, reload, onSecret }) {
  const [show, setShow] = useState(false);
  const act = async (fn, ok) => { try { const r = await fn(); toast.success(ok); reload(); return r; } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card testId={`webhook-card-${hook.id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0"><div className="truncate font-mono text-sm text-white">{hook.url}</div>
          <div className="mt-1 flex flex-wrap gap-1">{hook.events.map((e) => <span key={e} className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">{e}</span>)}</div></div>
        <div className="flex items-center gap-2">
          <StatusBadge status={hook.status} />
          <Switch checked={hook.status === "active"} aria-label="Active" data-testid="webhook-pause-toggle"
            onCheckedChange={(v) => act(() => api.patch(`/app/webhooks/${hook.id}`, { status: v ? "active" : "paused" }), v ? "Webhook resumed" : "Webhook paused")} />
          <Button size="sm" variant="ghost" title="Rotate secret" data-testid="webhook-rotate-button" className="hover:bg-white/5 hover:text-white"
            onClick={async () => { if (!window.confirm("Rotate the signing secret? The old secret stops working immediately.")) return; const r = await act(() => api.post(`/app/webhooks/${hook.id}/rotate`), "Secret rotated"); if (r) onSecret(r.data.secret); }}><RotateCw className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" title="Delete" data-testid="webhook-delete-button" className="text-red-300 hover:bg-red-500/10 hover:text-red-200"
            onClick={() => window.confirm("Delete this endpoint?") && act(() => api.delete(`/app/webhooks/${hook.id}`), "Webhook deleted")}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <button className="mt-4 text-xs text-purple-300" onClick={() => setShow(!show)} data-testid="webhook-deliveries-toggle">{show ? "Hide" : "Show"} delivery history</button>
      {show && <div className="mt-3"><Deliveries hook={hook} /></div>}
    </Card>
  );
}

function CreateHook({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ url: "", events: ["email.accepted", "email.failed"], description: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { const { data } = await api.post("/app/webhooks", form); onCreated(data.secret); setForm({ url: "", events: ["email.accepted", "email.failed"], description: "" }); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const toggle = (ev) => setForm((f) => ({ ...f, events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev] }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#0F0E13]" data-testid="webhook-create-dialog">
        <DialogHeader><DialogTitle>Add webhook endpoint</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <ErrorBanner error={error} testId="webhook-create-error" />
          <div className="space-y-1.5"><Label htmlFor="wu">HTTPS URL</Label><Input id="wu" type="url" required placeholder="https://api.example.com/webhooks/cario" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="webhook-url" /></div>
          <fieldset className="space-y-2"><legend className="mb-1 text-sm font-medium">Events</legend>
            {EVENTS.map((ev) => <label key={ev} className="flex items-center gap-2 font-mono text-xs text-zinc-300"><Checkbox checked={form.events.includes(ev)} onCheckedChange={() => toggle(ev)} data-testid={`webhook-event-${ev.replace(".", "-")}`} />{ev}</label>)}
          </fieldset>
          <div className="space-y-1.5"><Label htmlFor="wd">Description</Label><Input id="wd" maxLength={200} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="webhook-description" /></div>
          <DialogFooter><Button type="submit" disabled={busy || !form.events.length} data-testid="webhook-create-submit">Create endpoint</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Webhooks() {
  const { t } = useT();
  const { can } = useAuth();
  if (!can("admin")) {
    return <div><PageHeader title={t("nav.webhooks")} /><Notice tone="info" title="Admin role required" testId="webhooks-role-notice">Only workspace owners and admins can view and manage webhook endpoints.</Notice></div>;
  }
  return <WebhooksAdmin />;
}

function WebhooksAdmin() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState(null);
  const { data, error, loading, reload } = useLoad(() => api.get("/app/webhooks").then((r) => r.data));
  const status = useLoad(() => api.get("/app/sending-status").then((r) => r.data));
  return (
    <div>
      <PageHeader title={t("nav.webhooks")} description={t("desc.webhooks")}
        actions={<Button onClick={() => setOpen(true)} data-testid="webhook-create-button"><Plus className="mr-1 h-4 w-4" />Add endpoint</Button>} />
      <div className="space-y-6">
        {status.data && !status.data.delivery_events_configured && (
          <Notice tone="warn" title="Delivery events not configured" testId="events-not-configured">
            email.delivered, email.bounced and email.complained are only sent when Amazon SES event publishing is configured. Until then only email.accepted, email.failed, and (when tracking is on) email.opened, email.clicked and email.unsubscribed fire.
          </Notice>
        )}
        <ErrorBanner error={error} />
        {loading ? <Spinner /> : data && (data.webhooks.length === 0 ? <EmptyState icon={Webhook} title="No webhook endpoints" body="Add an HTTPS endpoint to receive signed events." testId="webhooks-empty" />
          : data.webhooks.map((h) => <HookCard key={h.id} hook={h} reload={reload} onSecret={setSecret} />))}
      </div>
      <CreateHook open={open} onClose={() => setOpen(false)} onCreated={(s) => { setOpen(false); setSecret(s); reload(); }} />
      <SecretReveal open={!!secret} secret={secret} title="Webhook signing secret" onClose={() => setSecret(null)} testId="webhook-secret-reveal" />
    </div>
  );
}
